import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Input,
  Kbd,
  Label,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  Text,
  Textarea,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  toastManager,
  type EntitySelection,
} from "@salestrak/ui";

import {
  CURRENCY_OPTIONS,
  DEFAULT_NUMBER_DECIMAL_PLACES,
  MAX_NUMBER_DECIMAL_PLACES,
  numberDecimalPlaces,
  numberThousandsSeparator,
} from "@salestrak/custom-fields";

// The decimal-places choices a number field offers, 0 through the package
// cap. Each example follows the field's grouping choice, so it shows the
// number as the field will.
function decimalPlacesOptions(grouping: boolean) {
  return Array.from({ length: MAX_NUMBER_DECIMAL_PLACES + 1 }, (_, places) => ({
    value: String(places),
    label:
      places === 0
        ? "0 — whole numbers"
        : `${places} — ${(1234.5678).toLocaleString(undefined, {
            minimumFractionDigits: places,
            maximumFractionDigits: places,
            useGrouping: grouping,
          })}`,
  }));
}
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useFieldTypeSelectorConfig } from "#app/lib/hooks/use-field-type-order";
import { FIELD_RENDERERS } from "#app/components/custom-fields/custom-field-renderers";
import {
  ChoiceOptionsEditor,
  asOptionSortMode,
  makeOptionId,
  type OptionRow,
  type OptionSortMode,
} from "#app/components/custom-fields/choice-options-editor";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type {
  CreateCustomFieldInput,
  CustomFieldConfigInput,
  CustomFieldOptionInput,
  CustomFieldType,
  UpdateCustomFieldInput,
} from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Edit takes the DEFINITION, not the manager's list row: the form and the
// danger-zone delete read only definition fields, so any surface that holds a
// field's definition (a view's Fields page, a column header) can open it.
export type CustomFieldFormMode =
  | {
      mode: "create";
      type: CustomFieldType;
      seed?: CustomFieldDef | undefined;
    }
  | { mode: "edit"; field: CustomFieldDef };

/**
 * Where the form is seated. `panel` is the Custom Field Manager's overlay:
 * the form draws its own 48px header (the type as title, create mode's type
 * switcher, a close) and scrolls its body itself. `page` is a page of a
 * Customize view panel: the panel's shell draws the header — title, Back,
 * the type switcher — and scrolls, so the form renders headerless and in
 * flow, and the TYPE is the host's: a create form follows `formMode.type`
 * and resets its type-specific state when the host switches it.
 */
export type CustomFieldFormPresentation = "panel" | "page";

type CustomFieldFormDialogProps = Omittable<{
  formMode: CustomFieldFormMode | null;
  presentation?: CustomFieldFormPresentation;
  onClose: () => void;
  onCreate: (input: CreateCustomFieldInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateCustomFieldInput) => Promise<unknown>;
  // Absent when the viewer lacks the delete.items grant — the danger zone
  // hides entirely (see the interlock note in the manage dialog).
  onDelete?: (field: CustomFieldDef) => void;
  // Reports whether the form deviates from its initial state, so the host
  // can route dismissals through an unsaved-changes confirm.
  onDirtyChange?: (dirty: boolean) => void;
}>;

// ---------------------------------------------------------------------------
// Field type categories
// ---------------------------------------------------------------------------

// The two option-bearing types. `select` is single BY TYPE — no cardinality
// toggle; a multi option-pick is what Multi Select (`labels`) is for.
const SELECT_TYPES = new Set<CustomFieldType>(["select", "labels"]);

// Every type accepts an optional default value except member — its candidate
// list is workspace data, not authored on the field — photos (per-issue
// uploads), formula (its value is computed, never seeded), and the option
// types: their per-option "Set default" chip IS the default control, so a
// second picker here would duplicate it.
const NO_DEFAULT_TYPES = new Set<CustomFieldType>(["member", "photos", "formula"]);

const MAX_OPTION_LABEL_LENGTH = 255;

// A default value is "empty" (omit from config) when blank for its type.
// Checkbox is special: `false` is a real default, only `null` is empty.
function isDefaultValueEmpty(type: CustomFieldType, value: unknown): boolean {
  if (value == null) return true;
  if (type === "checkbox") return false;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (type === "address") return Object.keys(value as object).length === 0;
  return false;
}

// Filter a select/labels default (an array of option values) down to values
// still present in the option list. Returns the input unchanged for other
// types.
function pruneSelectDefault(
  type: CustomFieldType,
  value: unknown,
  optionValues: Set<string> | null,
): unknown {
  if (!optionValues) return value;
  if (SELECT_TYPES.has(type) && Array.isArray(value)) {
    return value.filter((v) => typeof v === "string" && optionValues.has(v));
  }
  return value;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldFormDialog({
  formMode,
  presentation = "panel",
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  onDirtyChange,
}: CustomFieldFormDialogProps) {
  if (!formMode) return null;

  // A page-seated create form keeps its mount across a type switch — the
  // host changes the type and the name typed so far survives; the panel's
  // form owns the switch itself, so a new type from outside is a new form.
  const key =
    formMode.mode === "edit"
      ? `edit-${formMode.field.id}`
      : presentation === "page"
        ? "create"
        : `create-${formMode.type}`;

  return (
    <CustomFieldFormContent
      key={key}
      formMode={formMode}
      presentation={presentation}
      onClose={onClose}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onDirtyChange={onDirtyChange}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner form (remounts via key — no useEffect reset needed)
// ---------------------------------------------------------------------------

const CREATE_DEFAULTS = {
  name: "",
  description: "",
  currency: "usd",
  decimalPlaces: DEFAULT_NUMBER_DECIMAL_PLACES,
  // Bare digits, like a spreadsheet's general format: an identifier or a
  // year never misreads as an amount, and a quantity opts into grouping.
  thousandsSeparator: false,
  defaultValue: null as unknown,
  multiple: true,
  includeTeams: false,
  // A new Date field is a calendar day; the time half is opted into.
  includeTime: false,
  // Off by default: opening option authorship to everyone is a deliberate
  // choice the creator makes, not something a new field ships with.
  allowOptionCreate: false,
  optionSort: "manual" as OptionSortMode,
  showOptionColors: true,
};

function initFormState(formMode: CustomFieldFormMode) {
  const f = formMode.mode === "create" ? formMode.seed : formMode.field;
  if (!f) {
    // No seeded blank row — the "+ Add option" draft row authors the first
    // option.
    return { ...CREATE_DEFAULTS, options: [] as OptionRow[] };
  }
  const existingOpts = f.config?.options;
  return {
    name: f.name,
    description: f.description ?? "",
    currency: f.config?.currency?.toLowerCase() ?? "usd",
    decimalPlaces: numberDecimalPlaces(f.config),
    thousandsSeparator: numberThousandsSeparator(f.config),
    defaultValue: f.config?.defaultValue ?? null,
    multiple: f.config?.multiple !== false,
    includeTeams: f.config?.includeTeams === true,
    includeTime: f.config?.includeTime === true,
    allowOptionCreate: f.config?.allowOptionCreate !== false,
    optionSort: asOptionSortMode(f.config?.optionSort),
    showOptionColors: f.config?.showOptionColors !== false,
    options: (existingOpts ?? []).map(
      (o): OptionRow => ({
        id: makeOptionId(),
        label: o.label ?? "",
        value: o.value ?? undefined,
        color: o.color ?? null,
      }),
    ),
  };
}

type CustomFieldFormContentProps = Omittable<{
  formMode: CustomFieldFormMode;
  presentation: CustomFieldFormPresentation;
  onClose: () => void;
  onCreate: (input: CreateCustomFieldInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateCustomFieldInput) => Promise<unknown>;
  onDelete?: (field: CustomFieldDef) => void;
  onDirtyChange?: (dirty: boolean) => void;
}>;

// ---------------------------------------------------------------------------
// Default value editor — reuses the live field renderer for the chosen type
// so each type gets its native editor (phone country picker, date picker,
// select dropdown, checkbox, etc.) for free.
// ---------------------------------------------------------------------------

function DefaultValueEditor({
  fieldType,
  currency,
  decimalPlaces,
  thousandsSeparator,
  includeTime,
  value,
  onChangeValue,
}: {
  fieldType: CustomFieldType;
  currency: string;
  decimalPlaces: number;
  thousandsSeparator: boolean;
  includeTime: boolean;
  value: unknown;
  onChangeValue: (value: unknown) => void;
}) {
  const Renderer = FIELD_RENDERERS[fieldType];
  if (!Renderer) return null;

  // Synthesize a field def from the in-progress form so the renderer sees
  // the same config the saved field will carry. The option types never reach
  // this editor — their per-option "Set default" chip is the default control.
  const field: CustomFieldDef = {
    __typename: "CustomField",
    id: "__default__",
    customFieldId: "__default__",
    name: "Default value",
    key: "__default__",
    description: null,
    type: fieldType,
    config: {
      __typename: "CustomFieldConfigView",
      min: null,
      max: null,
      step: null,
      decimalPlaces: fieldType === "number" ? decimalPlaces : null,
      thousandsSeparator: fieldType === "number" ? thousandsSeparator : null,
      defaultValue: null,
      multiline: null,
      includeTeams: null,
      includeTime: fieldType === "date" ? includeTime : null,
      currency: fieldType === "money" ? currency.toUpperCase() : null,
      multiple: null,
      options: null,
      allowOptionCreate: null,
      optionSort: null,
      showOptionColors: null,
    },
  };

  // No `isActive`: this is a passive form control, so the select/date
  // renderers must not auto-open their dropdown/picker on mount.
  return (
    <Renderer
      field={field}
      value={value}
      onChangeValue={onChangeValue}
      surface="outline"
    />
  );
}

function CustomFieldFormContent({
  formMode,
  presentation,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  onDirtyChange,
}: CustomFieldFormContentProps) {
  const isCreate = formMode.mode === "create";
  const [fieldType, setFieldType] = useState<CustomFieldType>(
    formMode.mode === "create"
      ? formMode.type
      : (formMode.field.type as CustomFieldType),
  );

  const typeMeta = getFieldTypeMeta(fieldType);
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();

  // --- Form state (initialized from formMode, resets on remount via key) ---
  const [formInit] = useState(() => initFormState(formMode));
  const [name, setName] = useState(formInit.name);
  const [description, setDescription] = useState(formInit.description);
  const [defaultValue, setDefaultValue] = useState<unknown>(formInit.defaultValue);
  const [currency, setCurrency] = useState(formInit.currency);
  const [decimalPlaces, setDecimalPlaces] = useState(formInit.decimalPlaces);
  const [thousandsSeparator, setThousandsSeparator] = useState(
    formInit.thousandsSeparator,
  );
  const decimalOptions = useMemo(
    () => decimalPlacesOptions(thousandsSeparator),
    [thousandsSeparator],
  );
  const [multiple, setMultiple] = useState(formInit.multiple);
  const [includeTeams, setIncludeTeams] = useState(formInit.includeTeams);
  const [includeTime, setIncludeTime] = useState(formInit.includeTime);
  const [allowOptionCreate, setAllowOptionCreate] = useState(
    formInit.allowOptionCreate,
  );
  const [optionSort, setOptionSort] = useState<OptionSortMode>(
    formInit.optionSort,
  );
  const [showOptionColors, setShowOptionColors] = useState(
    formInit.showOptionColors,
  );
  const [options, setOptions] = useState<OptionRow[]>(formInit.options);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // The option types store their defaults as an array of option values in
  // defaultValue — the per-option "Set default" chips edit this view of it.
  const selectDefaults = useMemo(
    () =>
      Array.isArray(defaultValue)
        ? defaultValue.filter((v): v is string => typeof v === "string")
        : [],
    [defaultValue],
  );
  const handleDefaultsChange = useCallback((next: string[]) => {
    setDefaultValue(next.length ? next : null);
  }, []);

  // --- Dirty tracking: any deviation from the mount-time snapshot. Option
  // rows compare by label + persisted value + color (ids are mount-local),
  // and the default value by structure — it is a per-type JSON shape. ---
  const initialFieldType =
    formMode.mode === "create"
      ? formMode.type
      : (formMode.field.type as CustomFieldType);
  const dirty = useMemo(() => {
    const optionsKey = (list: OptionRow[]) =>
      JSON.stringify(list.map((o) => [o.label, o.value ?? null, o.color ?? null]));
    return (
      fieldType !== initialFieldType ||
      name !== formInit.name ||
      description !== formInit.description ||
      currency !== formInit.currency ||
      decimalPlaces !== formInit.decimalPlaces ||
      thousandsSeparator !== formInit.thousandsSeparator ||
      multiple !== formInit.multiple ||
      includeTeams !== formInit.includeTeams ||
      includeTime !== formInit.includeTime ||
      allowOptionCreate !== formInit.allowOptionCreate ||
      optionSort !== formInit.optionSort ||
      showOptionColors !== formInit.showOptionColors ||
      optionsKey(options) !== optionsKey(formInit.options) ||
      JSON.stringify(defaultValue ?? null) !==
        JSON.stringify(formInit.defaultValue ?? null)
    );
  }, [
    fieldType,
    initialFieldType,
    name,
    description,
    currency,
    decimalPlaces,
    thousandsSeparator,
    multiple,
    includeTeams,
    includeTime,
    allowOptionCreate,
    optionSort,
    showOptionColors,
    options,
    defaultValue,
    formInit,
  ]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Create-mode type switch: type-specific state resets to the create
  // defaults — a half-built option list or default value from the previous
  // type must never leak into the new one. The name survives: it is the
  // field's, not the type's.
  const switchType = useCallback((type: CustomFieldType) => {
    setFieldType(type);
    setOptions([]);
    setCurrency(CREATE_DEFAULTS.currency);
    setDecimalPlaces(CREATE_DEFAULTS.decimalPlaces);
    setThousandsSeparator(CREATE_DEFAULTS.thousandsSeparator);
    setDefaultValue(CREATE_DEFAULTS.defaultValue);
    setMultiple(CREATE_DEFAULTS.multiple);
    setIncludeTeams(CREATE_DEFAULTS.includeTeams);
    setIncludeTime(CREATE_DEFAULTS.includeTime);
    setAllowOptionCreate(CREATE_DEFAULTS.allowOptionCreate);
    setOptionSort(CREATE_DEFAULTS.optionSort);
    setShowOptionColors(CREATE_DEFAULTS.showOptionColors);
    setNameError(null);
    setOptionsError(null);
  }, []);
  // The header trigger's pick (panel presentation).
  const handleTypeSwitch = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected) switchType(selected.id as CustomFieldType);
    },
    [switchType],
  );
  // A page-seated create form follows the host's type: the shell's title
  // trigger switches it, and the form resets here rather than remounting so
  // the name typed so far stays.
  const hostType =
    presentation === "page" && formMode.mode === "create"
      ? formMode.type
      : null;
  const appliedHostTypeRef = useRef(hostType);
  useEffect(() => {
    if (hostType === null || appliedHostTypeRef.current === hostType) return;
    appliedHostTypeRef.current = hostType;
    switchType(hostType);
  }, [hostType, switchType]);

  // --- Validation ---
  const validate = useCallback((): string | null => {
    if (!name.trim()) return "Name is required";
    if (SELECT_TYPES.has(fieldType)) {
      const validOptions = options.filter((o) => o.label.trim());
      if (validOptions.length === 0) return "At least one option is required";
      const labels = validOptions.map((o) => o.label.trim().toLowerCase());
      if (new Set(labels).size !== labels.length)
        return "Option labels must be unique";
      if (validOptions.some((o) => o.label.trim().length > MAX_OPTION_LABEL_LENGTH))
        return `Option labels must be ${MAX_OPTION_LABEL_LENGTH} characters or fewer`;
    }
    return null;
  }, [name, fieldType, options]);

  const showsChoicesSection = SELECT_TYPES.has(fieldType);

  // --- Build config ---
  const buildConfig = useCallback((): CustomFieldConfigInput | undefined => {
    const config: CustomFieldConfigInput = {};
    let hasConfig = false;

    if (fieldType === "money" && currency) {
      config.currency = currency.toUpperCase();
      hasConfig = true;
    }
    if (fieldType === "number") {
      config.decimalPlaces = decimalPlaces;
      // Always written: flipping back to the default (off) must still
      // overwrite a stored `true`.
      config.thousandsSeparator = thousandsSeparator;
      hasConfig = true;
    }

    let optionValues: Set<string> | null = null;
    if (SELECT_TYPES.has(fieldType)) {
      const validOptions: CustomFieldOptionInput[] = options
        .filter((o) => o.label.trim())
        // Preserve an existing option's persisted value; only derive a fresh
        // value from the label for newly-added options. Renaming a saved
        // option keeps its value stable so stored issue values still resolve.
        .map((o) => ({
          label: o.label.trim(),
          value: o.value ?? o.label.trim(),
          color: o.color ?? null,
        }));
      if (validOptions.length > 0) {
        config.options = validOptions;
      }
      optionValues = new Set(validOptions.map((o) => o.value));
      // These keys are ALWAYS written: the update path only replaces the
      // keys the input carries, so flipping back to a default (true /
      // "manual") must still overwrite the stored row.
      config.allowOptionCreate = allowOptionCreate;
      config.optionSort = optionSort;
      config.showOptionColors = showOptionColors;
      hasConfig = true;
    }

    if (fieldType === "member") {
      config.multiple = multiple;
      config.includeTeams = includeTeams;
      hasConfig = true;
    } else if (fieldType === "photos") {
      config.multiple = multiple;
      hasConfig = true;
    } else if (fieldType === "date") {
      // Always written: the update path only replaces the keys the input
      // carries, so switching the time half back off must overwrite the
      // stored row.
      config.includeTime = includeTime;
      hasConfig = true;
    }

    if (fieldType === "member") {
      // member never carries an authored default
    } else if (!isDefaultValueEmpty(fieldType, defaultValue)) {
      // member fields don't author a default; every other type can. For the
      // option types, drop a default no longer backed by an option so the
      // API doesn't reject it.
      const pruned = pruneSelectDefault(fieldType, defaultValue, optionValues);
      if (!isDefaultValueEmpty(fieldType, pruned)) {
        // defaultValue is the GraphQL Object scalar (any shape per type).
        config.defaultValue = pruned as CustomFieldConfigInput["defaultValue"];
        hasConfig = true;
      }
    }

    return hasConfig ? config : undefined;
  }, [
    fieldType,
    currency,
    decimalPlaces,
    thousandsSeparator,
    options,
    multiple,
    includeTeams,
    includeTime,
    allowOptionCreate,
    optionSort,
    showOptionColors,
    defaultValue,
  ]);

  // --- Save ---
  // Saved options this edit drops. Projects store an option by its value, so
  // a dropped option empties that answer on every Project that holds it —
  // the save asks first and names them.
  const [pendingRemoval, setPendingRemoval] = useState<string[] | null>(null);
  const removedOptionLabels = useCallback((): string[] => {
    if (!SELECT_TYPES.has(fieldType)) return [];
    const kept = new Set(
      options.filter((o) => o.label.trim() && o.value).map((o) => o.value),
    );
    return formInit.options
      .filter((o) => o.value && !kept.has(o.value))
      .map((o) => o.label);
  }, [fieldType, options, formInit.options]);

  const handleSave = useCallback(async (removalConfirmed = false) => {
    // Clear previous errors
    setNameError(null);
    setOptionsError(null);

    const error = validate();
    if (error) {
      // Set inline errors on the relevant fields
      if (error === "Name is required") {
        setNameError(error);
      } else {
        setOptionsError(error);
      }
      toastManager.add({ title: error, type: "error" });
      return;
    }

    if (formMode.mode !== "create" && !removalConfirmed) {
      const removed = removedOptionLabels();
      if (removed.length > 0) {
        setPendingRemoval(removed);
        return;
      }
    }
    setPendingRemoval(null);

    setSaving(true);
    try {
      if (formMode.mode === "create") {
        const input: CreateCustomFieldInput = {
          name: name.trim(),
          type: fieldType,
          description: description.trim() || undefined,
          config: buildConfig(),
        };
        // A refusal (a taken name) was toasted by the hook; the form stays.
        if (!(await onCreate(input))) return;
      } else {
        const input: UpdateCustomFieldInput = {
          name: name.trim(),
          description: description.trim() || undefined,
          config: buildConfig(),
        };
        if (!(await onUpdate(formMode.field.id, input))) return;
      }
      onClose();
    } catch {
      // Errors are toasted by the mutation hook
    } finally {
      setSaving(false);
    }
  }, [formMode, fieldType, name, description, validate, buildConfig, removedOptionLabels, onCreate, onUpdate, onClose]);

  const header = (
    <>
      {/* 48px header, no rule above — the panel's top edge is the dialog
          edge, and the bottom border sits INSIDE the 48px (border="bottom",
          not a Separator after the box) exactly like ManageDialogHeader, so
          the two rules meet across the seam. The TYPE is the title:
          create mode reopens the type picker from a ghost trigger (switching
          resets type-specific state); edit mode is plain text, no
          affordance — converting would silently drop config/options, and
          stored values don't map across types. */}
      <Box
        height={48}
        paddingX="sm"
        direction="row"
        align="center"
        justify="between"
        border="bottom"
      >
        {isCreate ? (
          <EntitySelector
            title="Field type"
            config={fieldTypeSelectorConfig}
            value={[]}
            onValueChange={handleTypeSwitch}
          >
            <EntitySelectorTrigger
              render={
                <Button variant="ghost" aria-label="Change field type">
                  <Text size="base" weight="medium">{typeMeta.label}</Text>
                  <Icon name="chevron-down" size="sm" color="muted" />
                </Button>
              }
            />
            <EntitySelectorContent align="start">
              <EntitySelectorInput />
              <EntitySelectorPool value="field-types" />
              <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
            </EntitySelectorContent>
          </EntitySelector>
        ) : (
          // Static — but on the same text line and size as the create-mode
          // trigger, via a spacer matching its inset.
          <Box paddingLeft="sm">
            <Text size="base" weight="medium">{typeMeta.label}</Text>
          </Box>
        )}
        {/* Through `render`: TooltipTrigger renders its own <button>, so a
            Button nested inside it would be a <button> within a <button>. */}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close panel"
                onClick={onClose}
              />
            }
          >
            <Icon name="drawer-closed-right" size="sm" color="muted" />
          </TooltipTrigger>
          <TooltipPopup>
            <Box align="center" direction="row" gap="xs">
              <Text size="xs">Close</Text>
              <Kbd>Esc</Kbd>
            </Box>
          </TooltipPopup>
        </Tooltip>
      </Box>
    </>
  );

  // The type's own toggles, rendered in More settings. Null for a type with
  // none, so the group (and its heading) is absent rather than empty.
  const typeToggles: React.ReactNode =
    fieldType === "number" ? (
      // Bare digits are the default so an identifier or a year never reads
      // as `20,231`; a quantity opts into grouping.
      <Label>
        <Switch
          checked={thousandsSeparator}
          onCheckedChange={setThousandsSeparator}
        />
        <Text size="sm" weight="normal">Show thousands separator</Text>
      </Label>
    ) : fieldType === "member" ? (
      // Cardinality + team visibility.
      <>
        <Label>
          <Switch checked={multiple} onCheckedChange={setMultiple} />
          <Text size="sm" weight="normal">
            {includeTeams ? "Allow multiple members/teams" : "Allow multiple members"}
          </Text>
        </Label>
        <Label>
          <Switch checked={includeTeams} onCheckedChange={setIncludeTeams} />
          <Text size="sm" weight="normal">Include teams</Text>
        </Label>
      </>
    ) : fieldType === "photos" ? (
      <Label>
        <Switch checked={multiple} onCheckedChange={setMultiple} />
        <Text size="sm" weight="normal">Allow multiple photos</Text>
      </Label>
    ) : fieldType === "date" ? (
      // A calendar day, or an instant with its time. One type either way:
      // stored values stay put when this flips.
      <Label>
        <Switch checked={includeTime} onCheckedChange={setIncludeTime} />
        <Text size="sm" weight="normal">Include time</Text>
      </Label>
    ) : null;

  const body = (
    <>
        {/* Main fields — no "General" group: the panel opens
            straight onto Field name. Only the secondary section collapses. */}
        <Box paddingX="md" paddingTop="md" paddingBottom="md">
          <Box gap="md">
            <Box gap="xs">
              <Box direction="row" align="center" gap="2xs">
                <Text size="sm" weight="medium" variant="muted">Field name</Text>
                <Text size="sm" variant="destructive">*</Text>
              </Box>
              <Input
                // outline chrome throughout this form: the drawer is
                // background-filled, so fields take the page fill and let
                // the border (hover-brightened) do the work.
                variant="outline"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (nameError) setNameError(null);
                }}
                placeholder="Field name"
                autoFocus
                // Required: red once the person has left it empty (blurred
                // or saved), not on a form they have not touched yet.
                onBlur={() => setNameTouched(true)}
                aria-invalid={(nameTouched && !name.trim()) || !!nameError}
              />
              {nameError ? (
                <Text size="xs" variant="destructive">{nameError}</Text>
              ) : null}
            </Box>

            {fieldType === "money" ? (
              <Box gap="xs">
                <Text size="sm" weight="medium" variant="muted">Currency</Text>
                <Select
                  items={CURRENCY_OPTIONS}
                  value={currency ?? null}
                  onValueChange={(next: string | null) => { if (next) setCurrency(next); }}
                >
                  <SelectTrigger aria-label="Currency" variant="outline">
                    <SelectValue placeholder="Select currency..." />
                  </SelectTrigger>
                  <SelectPopup>
                    {CURRENCY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              </Box>
            ) : null}

            {fieldType === "number" ? (
              <Box gap="xs">
                <Text size="sm" weight="medium" variant="muted">Decimal places</Text>
                <Select
                  items={decimalOptions}
                  value={String(decimalPlaces)}
                  onValueChange={(next: string | null) => {
                    if (next !== null) setDecimalPlaces(Number(next));
                  }}
                >
                  {/* A combobox takes no name from its content. */}
                  <SelectTrigger aria-label="Decimal places" variant="outline">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPopup>
                    {decimalOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
                <Text size="xs" variant="muted">
                  Extra decimals typed into a value round to this when it is saved.
                </Text>
              </Box>
            ) : null}

            {/* Default value — every type except member (its candidate list
                is workspace data, not authored here) and the option types
                (their per-option "Set default" chips ARE the default
                control). No "Optional" tag: the required-* markers carry the
                distinction. */}
            {!NO_DEFAULT_TYPES.has(fieldType) && !SELECT_TYPES.has(fieldType) ? (
              <Box gap="xs">
                <Text size="sm" weight="medium" variant="muted">Default value</Text>
                <DefaultValueEditor
                  fieldType={fieldType}
                  currency={currency}
                  decimalPlaces={decimalPlaces}
                  thousandsSeparator={thousandsSeparator}
                  includeTime={includeTime}
                  value={defaultValue}
                  onChangeValue={setDefaultValue}
                />
              </Box>
            ) : null}

            {/* Options — the option types, closing the main group (no
                separate Choices section). */}
            {showsChoicesSection ? (
              <ChoiceOptionsEditor
                label={`${typeMeta.label} options`}
                options={options}
                onOptionsChange={setOptions}
                defaults={selectDefaults}
                onDefaultsChange={handleDefaultsChange}
                // Single Select is single BY TYPE: setting a default replaces
                // the previous one.
                exclusiveDefault={fieldType === "select"}
                sort={optionSort}
                onSortChange={setOptionSort}
                error={optionsError}
              />
            ) : null}
          </Box>
        </Box>

        {/* More settings — collapsed by default: the description, the
            type's own toggles and the permission-shaped settings. The main
            group above keeps only what shapes the value itself (type,
            currency, decimals, default, options). */}
        <Separator />
        <Box paddingX="md" paddingTop="sm" paddingBottom="sm">
          <Accordion multiple>
            <AccordionItem value="more-settings" className="border-b-0">
              <AccordionTrigger className="py-2">
                <Text size="sm" weight="semibold">More settings and permissions</Text>
              </AccordionTrigger>
              <AccordionPanel>
                {/* md top gap: the first field needs clear air under the
                    group title — sm reads squashed against it. */}
                <Box paddingTop="md" paddingBottom="sm" gap="lg">
                  <Box gap="xs">
                    <Text size="sm" weight="medium" variant="muted">Description</Text>
                    <Textarea
                      variant="outline"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="Tell other users how to use this field"
                      rows={2}
                    />
                  </Box>
                  {/* Per-type toggles: ONE group headed "<Type> settings" for
                      every type, a sibling of Description. "Options" is
                      reserved for the choice list in the main group, so the
                      select types' toggles wear the same heading as the
                      rest. Settings-toggle grammar: the Switch leads and the
                      Label wraps switch + text, so a label press toggles. */}
                  {typeToggles ? (
                    <Box gap="sm">
                      <Text size="sm" weight="medium" variant="muted">
                        {typeMeta.label} settings
                      </Text>
                      {typeToggles}
                    </Box>
                  ) : null}
                  {showsChoicesSection ? (
                    <Box gap="sm">
                      <Text size="sm" weight="medium" variant="muted">
                        {typeMeta.label} settings
                      </Text>
                      {/* Settings-toggle grammar: the Switch leads; the Label
                          wraps switch + text so a label press toggles, while
                          the row itself (and the info glyph) stays inert. */}
                      <Box direction="row" align="center" gap="sm">
                        <Label>
                          <Switch
                            checked={allowOptionCreate}
                            onCheckedChange={setAllowOptionCreate}
                          />
                          <Text size="sm" weight="normal">Let anyone create options</Text>
                        </Label>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Icon name="info" size="sm" color="muted" />
                            }
                          />
                          <TooltipPopup side="top">
                            Anyone can create new options on the spot from the
                            picker
                          </TooltipPopup>
                        </Tooltip>
                      </Box>
                      {/* Colors are on by default; this switch is the INVERSE
                          of the stored showOptionColors flag. On, values render
                          as plain neutral chips so the field stops competing
                          for attention. Colors stay on each option, so this is
                          reversible. */}
                      <Box direction="row" align="center" gap="sm">
                        <Label>
                          <Switch
                            checked={!showOptionColors}
                            onCheckedChange={(hide) => setShowOptionColors(!hide)}
                          />
                          <Text size="sm" weight="normal">Hide option colors</Text>
                        </Label>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Icon name="info" size="sm" color="muted" />
                            }
                          />
                          <TooltipPopup side="top">
                            Values show their option&apos;s color in lists, boards
                            and details. Turn on for a plain look on a field that
                            shouldn&apos;t stand out.
                          </TooltipPopup>
                        </Tooltip>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
    </>
  );

  const footer = (
      <Box>
        <Separator />
        {/* Condensed footer: sm actions on a slim strip instead of
            full-size buttons swimming in lg padding. Edit mode's delete is a
            tinted trash icon at the LEFT (no Danger Zone section) — the
            confirm dialog still guards the actual delete. */}
        <Box paddingX="md" paddingY="sm">
          <Box direction="row" gap="sm" align="center">
            {!isCreate && onDelete ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      color="destructive"
                      variant="outline"
                      size="icon-sm"
                      aria-label="Delete field"
                      onClick={() => onDelete(formMode.field)}
                    >
                      <Icon name="trash" size="sm" />
                    </Button>
                  }
                />
                <TooltipPopup side="top">Delete field</TooltipPopup>
              </Tooltip>
            ) : null}
            <Box grow />
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
              <Text>Cancel</Text>
            </Button>
            <Button size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving}>
              <Text>{isCreate ? "Create" : "Save"}</Text>
            </Button>
          </Box>
        </Box>
        <AlertDialog
          open={pendingRemoval != null}
          onOpenChange={(open) => {
            if (!open) setPendingRemoval(null);
          }}
        >
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingRemoval?.length === 1
                  ? "Remove 1 option?"
                  : `Remove ${pendingRemoval?.length ?? 0} options?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {`Projects that have ${(pendingRemoval ?? [])
                  .map((label) => `"${label}"`)
                  .join(", ")} lose that value. This cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>
                Cancel
              </AlertDialogClose>
              <Button color="destructive" onClick={() => void handleSave(true)}>
                Remove and save
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </Box>
  );

  // A page renders in flow — the shell that hosts it draws the header and
  // owns the scroll, so a second header or a nested scroll region would
  // double both.
  if (presentation === "page") {
    return (
      <Box direction="column">
        {body}
        {footer}
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      {header}
      {/* ScrollArea owns its scrollbar chrome — there is nothing to tune from
          this call site. */}
      <ScrollArea>{body}</ScrollArea>
      {footer}
    </Box>
  );
}
